import type { EffectRegion } from "@client/types/vision";

/**
 * Build a binary mask from a polygon via the scanline / even-odd rule.
 * O(width * height) one-time. For 1024x1024 + 10 vertices, < 5 ms on M1.
 *
 * Even-odd handles self-intersecting polygons naturally; horizontal edges
 * are skipped to avoid double-counting at vertex scanlines.
 */
export function computePolygonMask(
	points: Array<{ x: number; y: number }>,
	width: number,
	height: number,
): Uint8Array {
	const mask = new Uint8Array(width * height);
	if (points.length < 3) return mask;

	const n = points.length;
	for (let y = 0; y < height; y++) {
		const xs: number[] = [];
		for (let i = 0; i < n; i++) {
			const a = points[i];
			const b = points[(i + 1) % n];
			if (!a || !b) continue;
			if (a.y === b.y) continue; // horizontal edge
			const yMin = Math.min(a.y, b.y);
			const yMax = Math.max(a.y, b.y);
			if (y < yMin || y >= yMax) continue;
			xs.push(a.x + ((y - a.y) * (b.x - a.x)) / (b.y - a.y));
		}
		xs.sort((p, q) => p - q);
		for (let k = 0; k + 1 < xs.length; k += 2) {
			const start = xs[k];
			const end = xs[k + 1];
			if (start === undefined || end === undefined) continue;
			const x0 = Math.max(0, Math.ceil(start));
			const x1 = Math.min(width, Math.floor(end));
			for (let x = x0; x < x1; x++) {
				mask[y * width + x] = 1;
			}
		}
	}
	return mask;
}

/** Bounding box of any region. Polygon: min/max over points. Rect: trivial. */
export function getRegionBounds(region: EffectRegion): {
	rx1: number;
	ry1: number;
	rx2: number;
	ry2: number;
} {
	if (region.type === "polygon") {
		let minX = Number.POSITIVE_INFINITY;
		let minY = Number.POSITIVE_INFINITY;
		let maxX = Number.NEGATIVE_INFINITY;
		let maxY = Number.NEGATIVE_INFINITY;
		for (const p of region.points) {
			if (p.x < minX) minX = p.x;
			if (p.y < minY) minY = p.y;
			if (p.x > maxX) maxX = p.x;
			if (p.y > maxY) maxY = p.y;
		}
		return { rx1: minX, ry1: minY, rx2: maxX, ry2: maxY };
	}
	return { rx1: region.x1, ry1: region.y1, rx2: region.x2, ry2: region.y2 };
}

/** Per-pixel inside-region test. O(1) for both rect and polygon. */
export function isInRegion(
	x: number,
	y: number,
	region: EffectRegion,
): boolean {
	if (region.type === "polygon") {
		return region.mask[y * region.maskWidth + x] === 1;
	}
	return x >= region.x1 && x < region.x2 && y >= region.y1 && y < region.y2;
}
